import React from 'react';
import { Helmet } from 'react-helmet-async';
import './Terms.css';

const Terms = () => {
  return (
    <>
      <Helmet>
        <title>Terms of Service - Safarnamah by Saket and Pallavi</title>
        <meta name="description" content="Terms of Service for Safarnamah - Read our terms and conditions for using our travel guide platform." />
      </Helmet>

      <div className="terms-page">
        <div className="terms-hero">
          <div className="container">
            <h1>Terms of Service</h1>
            <p className="last-updated">Last Updated: November 14, 2025</p>
          </div>
        </div>

        <div className="terms-content">
          <div className="container">
            <div className="terms-section">
              <h2>Agreement to Terms</h2>
              <p>
                Welcome to Safarnamah by Saket and Pallavi. These Terms of Service ("Terms") govern your access to and use of our website, services, and content. By accessing or using Safarnamah, you agree to be bound by these Terms.
              </p>
              <p>
                If you do not agree to these Terms, you must not access or use our website. We reserve the right to modify these Terms at any time, and your continued use of the website constitutes acceptance of any changes.
              </p>
            </div>

            <div className="terms-section">
              <h2>Use of Our Services</h2>
              
              <h3>Eligibility</h3>
              <p>
                You must be at least 18 years old to use our services. By using Safarnamah, you represent and warrant that you meet this age requirement and have the legal capacity to enter into these Terms.
              </p>

              <h3>Permitted Use</h3>
              <p>You may use our website for the following purposes:</p>
              <ul>
                <li>Browsing travel guides, destination information, and blog content</li>
                <li>Planning trips and researching travel destinations</li>
                <li>Subscribing to our newsletter and communications</li>
                <li>Engaging with our chatbot for travel assistance</li>
                <li>Contacting us for inquiries and support</li>
              </ul>

              <h3>Prohibited Activities</h3>
              <p>You agree not to:</p>
              <ul>
                <li>Use the website for any illegal or unauthorized purpose</li>
                <li>Violate any laws or regulations in your jurisdiction</li>
                <li>Infringe upon the intellectual property rights of Safarnamah or others</li>
                <li>Transmit viruses, malware, or other harmful code</li>
                <li>Attempt to gain unauthorized access to our systems or networks</li>
                <li>Scrape, harvest, or collect data from our website without permission</li>
                <li>Impersonate any person or entity or falsely represent your affiliation</li>
                <li>Interfere with or disrupt the operation of our website</li>
                <li>Use automated systems (bots, scripts) without our express permission</li>
                <li>Post or transmit spam, advertising, or promotional materials without consent</li>
              </ul>
            </div>

            <div className="terms-section">
              <h2>Intellectual Property Rights</h2>
              
              <h3>Our Content</h3>
              <p>
                All content on Safarnamah, including but not limited to text, images, photographs, videos, graphics, logos, designs, and software, is the property of Safarnamah by Saket and Pallavi or its licensors and is protected by copyright, trademark, and other intellectual property laws.
              </p>

              <h3>License to Use</h3>
              <p>
                We grant you a limited, non-exclusive, non-transferable, revocable license to access and use our website for personal, non-commercial purposes. You may not:
              </p>
              <ul>
                <li>Reproduce, distribute, or display our content without permission</li>
                <li>Modify, adapt, or create derivative works from our content</li>
                <li>Use our content for commercial purposes without a license</li>
                <li>Remove copyright notices or proprietary markings</li>
              </ul>

              <h3>User-Generated Content</h3>
              <p>
                If you submit comments, reviews, photos, or other content to Safarnamah, you grant us a worldwide, royalty-free, perpetual, irrevocable license to use, reproduce, modify, publish, and distribute such content in any media. You represent that you own or have the necessary rights to the content you submit.
              </p>
            </div>

            <div className="terms-section">
              <h2>Third-Party Services and Links</h2>
              <p>
                Our website may contain links to third-party websites, services, or resources that are not owned or controlled by Safarnamah. These include:
              </p>
              <ul>
                <li>Hotel booking platforms and accommodation providers</li>
                <li>Tour operators and activity providers</li>
                <li>Transportation services and airlines</li>
                <li>Travel insurance providers</li>
                <li>Social media platforms</li>
              </ul>
              <p>
                We are not responsible for the content, policies, or practices of third-party websites. Any dealings with third parties are solely between you and such third party. We recommend reviewing the terms and policies of any third-party services you use.
              </p>
            </div>

            <div className="terms-section">
              <h2>Bookings and Reservations</h2>
              <p>
                If you make bookings or reservations through links or services on our website:
              </p>
              <ul>
                <li>You are entering into a contract with the third-party service provider, not with Safarnamah</li>
                <li>We are not responsible for the quality, safety, or legality of services provided</li>
                <li>We do not guarantee the accuracy of pricing, availability, or descriptions</li>
                <li>Cancellation, refund, and modification policies are determined by the service provider</li>
                <li>You should verify all details directly with the service provider before booking</li>
              </ul>
              <p>
                Safarnamah acts as an informational platform and may receive affiliate commissions from bookings made through our links, at no additional cost to you.
              </p>
            </div>

            <div className="terms-section">
              <h2>Disclaimer of Warranties</h2>
              <p>
                <strong>OUR WEBSITE AND SERVICES ARE PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND.</strong> We disclaim all warranties, express or implied, including but not limited to:
              </p>
              <ul>
                <li>Warranties of merchantability and fitness for a particular purpose</li>
                <li>Warranties regarding accuracy, reliability, or completeness of content</li>
                <li>Warranties that the website will be uninterrupted, secure, or error-free</li>
                <li>Warranties that defects will be corrected</li>
              </ul>
              <p>
                Travel information, recommendations, and advice are provided for informational purposes only and may not reflect current conditions. You are responsible for verifying all information before making travel decisions.
              </p>
            </div>

            <div className="terms-section">
              <h2>Limitation of Liability</h2>
              <p>
                <strong>TO THE MAXIMUM EXTENT PERMITTED BY LAW, SAFARNAMAH AND ITS OWNERS, EMPLOYEES, AND AFFILIATES SHALL NOT BE LIABLE FOR:</strong>
              </p>
              <ul>
                <li>Any indirect, incidental, special, consequential, or punitive damages</li>
                <li>Loss of profits, revenue, data, or business opportunities</li>
                <li>Personal injury, property damage, or emotional distress</li>
                <li>Errors, omissions, or inaccuracies in content</li>
                <li>Actions or omissions of third-party service providers</li>
                <li>Travel delays, cancellations, or disruptions</li>
                <li>Loss or theft of belongings during travel</li>
                <li>Health issues, accidents, or emergencies during travel</li>
              </ul>
              <p>
                Our total liability for any claims arising from your use of the website shall not exceed the amount you paid to us (if any) in the twelve months preceding the claim.
              </p>
            </div>

            <div className="terms-section">
              <h2>Indemnification</h2>
              <p>
                You agree to indemnify, defend, and hold harmless Safarnamah, Saket and Pallavi, and our affiliates, officers, directors, employees, and agents from any claims, liabilities, damages, losses, costs, or expenses (including reasonable attorneys' fees) arising from:
              </p>
              <ul>
                <li>Your use of our website or services</li>
                <li>Your violation of these Terms</li>
                <li>Your violation of any rights of another party</li>
                <li>Your content or submissions to the website</li>
                <li>Your travel activities or decisions based on our content</li>
              </ul>
            </div>

            <div className="terms-section">
              <h2>Travel Advice and Responsibility</h2>
              <p>
                <strong>Important Travel Disclaimers:</strong>
              </p>
              <ul>
                <li><strong>Personal Responsibility:</strong> You are solely responsible for your travel decisions, safety, and well-being</li>
                <li><strong>Verify Information:</strong> Always verify travel advisories, visa requirements, and safety conditions before traveling</li>
                <li><strong>Health Precautions:</strong> Consult healthcare professionals about vaccinations, health risks, and travel insurance</li>
                <li><strong>Local Laws:</strong> Research and comply with local laws, customs, and regulations of your destinations</li>
                <li><strong>Government Advisories:</strong> Check your government's travel advisories and register with embassies when traveling</li>
                <li><strong>Insurance:</strong> Obtain appropriate travel insurance covering health, accidents, cancellations, and emergencies</li>
                <li><strong>Currency and Payments:</strong> Understand exchange rates, payment methods, and potential fees</li>
                <li><strong>Documentation:</strong> Ensure passports, visas, and other documents are valid and up-to-date</li>
              </ul>
            </div>

            <div className="terms-section">
              <h2>User Accounts</h2>
              <p>
                If you create an account with Safarnamah:
              </p>
              <ul>
                <li>You are responsible for maintaining the confidentiality of your account credentials</li>
                <li>You are responsible for all activities that occur under your account</li>
                <li>You must provide accurate, current, and complete information</li>
                <li>You must update your information promptly if it changes</li>
                <li>You must notify us immediately of any unauthorized access to your account</li>
              </ul>
              <p>
                We reserve the right to suspend or terminate accounts that violate these Terms or engage in prohibited activities.
              </p>
            </div>

            <div className="terms-section">
              <h2>Privacy and Data Protection</h2>
              <p>
                Your use of our website is also governed by our <a href="/privacy">Privacy Policy</a>, which explains how we collect, use, and protect your personal information. By using Safarnamah, you consent to our data practices as described in the Privacy Policy.
              </p>
            </div>

            <div className="terms-section">
              <h2>Newsletter and Communications</h2>
              <p>
                By subscribing to our newsletter, you consent to receive:
              </p>
              <ul>
                <li>Travel guides and destination recommendations</li>
                <li>Blog updates and travel stories</li>
                <li>Tips, advice, and travel inspiration</li>
                <li>Promotional offers from partners (with your consent)</li>
              </ul>
              <p>
                You may unsubscribe at any time by clicking the unsubscribe link in emails or updating your account preferences.
              </p>
            </div>

            <div className="terms-section">
              <h2>Modifications to Service</h2>
              <p>
                We reserve the right to:
              </p>
              <ul>
                <li>Modify, suspend, or discontinue any aspect of our website or services</li>
                <li>Change features, functionality, or content at any time</li>
                <li>Limit access to certain features or content</li>
                <li>Update these Terms as needed</li>
              </ul>
              <p>
                We will make reasonable efforts to notify users of significant changes, but we are not obligated to do so.
              </p>
            </div>

            <div className="terms-section">
              <h2>Termination</h2>
              <p>
                We may terminate or suspend your access to our website immediately, without prior notice or liability, for any reason, including:
              </p>
              <ul>
                <li>Violation of these Terms</li>
                <li>Engaging in prohibited activities</li>
                <li>Fraudulent or illegal conduct</li>
                <li>At our sole discretion</li>
              </ul>
              <p>
                Upon termination, your right to use the website will cease immediately. Provisions that by their nature should survive termination shall remain in effect.
              </p>
            </div>

            <div className="terms-section">
              <h2>Governing Law and Dispute Resolution</h2>
              <p>
                These Terms shall be governed by and construed in accordance with the laws of India, without regard to conflict of law provisions.
              </p>
              <p>
                Any disputes arising from these Terms or your use of the website shall be resolved through:
              </p>
              <ul>
                <li><strong>Negotiation:</strong> Good faith efforts to resolve disputes amicably</li>
                <li><strong>Mediation:</strong> If negotiation fails, mediation by a mutually agreed mediator</li>
                <li><strong>Arbitration:</strong> Binding arbitration in accordance with Indian arbitration laws</li>
                <li><strong>Jurisdiction:</strong> Courts in [Your City/State] shall have exclusive jurisdiction</li>
              </ul>
            </div>

            <div className="terms-section">
              <h2>Severability</h2>
              <p>
                If any provision of these Terms is found to be invalid, illegal, or unenforceable, the remaining provisions shall continue in full force and effect. The invalid provision shall be modified to the minimum extent necessary to make it valid and enforceable.
              </p>
            </div>

            <div className="terms-section">
              <h2>Entire Agreement</h2>
              <p>
                These Terms, together with our Privacy Policy and any other legal notices published on our website, constitute the entire agreement between you and Safarnamah regarding the use of our website and services.
              </p>
            </div>

            <div className="terms-section">
              <h2>Contact Information</h2>
              <p>
                If you have any questions about these Terms of Service, please contact us:
              </p>
              <div className="contact-details">
                <p><strong>Email:</strong> legal@safarnamah.in</p>
                <p><strong>General Inquiries:</strong> contact@safarnamah.in</p>
                <p><strong>Website:</strong> <a href="https://www.safarnamah.in">www.safarnamah.in</a></p>
              </div>
            </div>

            <div className="terms-section acknowledgment">
              <h2>Acknowledgment and Acceptance</h2>
              <p>
                By clicking "I Accept," accessing, or using Safarnamah, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service. If you do not agree to these Terms, you must immediately cease using our website.
              </p>
              <p className="signature">
                — The Safarnamah Team<br />
                Saket and Pallavi
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Terms;
